import React, { useEffect, useState } from 'react';
import {
  Button, DatePicker, Form, InputNumber, Popconfirm,
  Select, Space, Table, Typography, message, Modal, Input,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  MaintenanceLog as ILog, getMaintenanceLogs, createMaintenanceLog, deleteMaintenanceLog,
} from '../api/maintenance';
import { WarehouseItem, getWarehouseItems } from '../api/warehouse-items';
import api from '../api/client';

const { Text } = Typography;

interface Employee { id: number; first_name: string; last_name: string; }

interface Props {
  equipmentId: number;
}

const MaintenanceLogComponent: React.FC<Props> = ({ equipmentId }) => {
  const [logs, setLogs] = useState<ILog[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [warehouseItems, setWarehouseItems] = useState<WarehouseItem[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [form] = Form.useForm();

  const loadLogs = async () => {
    setLoading(true);
    try {
      setLogs(await getMaintenanceLogs(equipmentId));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    getWarehouseItems().then(setWarehouseItems);
    api.get<Employee[]>('/employees').then(r => {
      const d = r.data as any;
      setEmployees(Array.isArray(d) ? d : d.items ?? []);
    });
  }, [equipmentId]);

  const openModal = () => {
    form.resetFields();
    form.setFieldsValue({ date: dayjs(), items: [{}] });
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        equipment_id: equipmentId,
        date: values.date.format('YYYY-MM-DD'),
        description: values.description,
        executor_id: values.executor_id ?? null,
        items: (values.items || [])
          .filter((i: any) => i?.warehouse_item_id)
          .map((i: any) => ({
            warehouse_item_id: i.warehouse_item_id,
            quantity: i.quantity,
            price_per_unit: i.price_per_unit,
          })),
      };
      const created = await createMaintenanceLog(payload);
      setLogs(prev => [created, ...prev]);
      setModalOpen(false);
      message.success('Запись добавлена');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка при сохранении');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMaintenanceLog(id);
      setLogs(prev => prev.filter(l => l.id !== id));
      message.success('Удалено');
    } catch {
      message.error('Ошибка при удалении');
    }
  };

  const columns = [
    { title: 'Дата', dataIndex: 'date', key: 'date', width: 110 },
    { title: 'Описание', dataIndex: 'description', key: 'desc' },
    {
      title: 'Исполнитель',
      key: 'executor',
      width: 160,
      render: (_: any, r: ILog) =>
        r.executor ? `${r.executor.last_name} ${r.executor.first_name[0]}.` : '—',
    },
    {
      title: 'Запчасти',
      key: 'items',
      render: (_: any, r: ILog) =>
        r.items.length === 0 ? '—' : r.items.map(i => (
          <div key={i.id}>
            {i.warehouse_item.name} × {i.quantity} {i.warehouse_item.unit}
            {' — '}{(+i.price_per_unit * +i.quantity).toFixed(2)} ₽
          </div>
        )),
    },
    {
      title: '',
      key: 'del',
      width: 50,
      render: (_: any, r: ILog) => (
        <Popconfirm title="Удалить запись?" onConfirm={() => handleDelete(r.id)} okText="Да" cancelText="Нет">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12, justifyContent: 'flex-end', width: '100%' }}>
        <Button icon={<PlusOutlined />} onClick={openModal}>Добавить запись</Button>
      </Space>

      <Table
        columns={columns}
        dataSource={logs}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'Записей нет' }}
      />

      <Modal
        title="Новая запись журнала"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText="Сохранить"
        cancelText="Отмена"
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="date" label="Дата" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="description" label="Что сделано" rules={[{ required: true, message: 'Опишите работу' }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="executor_id" label="Исполнитель">
            <Select
              allowClear
              placeholder="Не указан"
              options={employees.map(e => ({ value: e.id, label: `${e.last_name} ${e.first_name}` }))}
            />
          </Form.Item>

          <Text strong>Запчасти со склада:</Text>
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Space key={key} align="baseline" style={{ display: 'flex', marginTop: 8 }}>
                    <Form.Item name={[name, 'warehouse_item_id']} style={{ marginBottom: 0 }}>
                      <Select
                        placeholder="Позиция"
                        style={{ width: 200 }}
                        options={warehouseItems.map(w => ({ value: w.id, label: `${w.name} (${w.current_stock} ${w.unit})` }))}
                      />
                    </Form.Item>
                    <Form.Item name={[name, 'quantity']} style={{ marginBottom: 0 }}>
                      <InputNumber placeholder="Кол-во" min={0.001} precision={3} style={{ width: 90 }} />
                    </Form.Item>
                    <Form.Item name={[name, 'price_per_unit']} style={{ marginBottom: 0 }}>
                      <InputNumber placeholder="Цена/ед ₽" min={0} precision={2} style={{ width: 110 }} />
                    </Form.Item>
                    <Button danger size="small" onClick={() => remove(name)}>✕</Button>
                  </Space>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                  style={{ marginTop: 8 }}
                  size="small"
                >
                  Добавить запчасть
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default MaintenanceLogComponent;
